import QuestionOrganism from 'questionFlow/QuestionOrganism';
import { Button, Checkbox, Field, TextButton } from 'components';
import { useCallback, useState } from 'react';
import { useAnalyticsEvent } from 'monitoring/analyticsHooks';
import {
  DefaultIncomeQuestionComponentProps,
  StepNamesDefaultIncome,
} from 'questionFlow/flows/defaultIncome/types';
import goToKonfirAuthUrl from 'pages/Konfir/goToKonfirAuthUrl';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/solid';

const stepName = StepNamesDefaultIncome.konfir;

const cmsLocation = '/questions/income/Konfir';

const useContent = (t: TFunction<'translation'>) => ({
  title: t(`${cmsLocation}.title`),
  description: t(`${cmsLocation}.description`),
  button: t(`${cmsLocation}.buttonText`),
  skipDescription: t(`${cmsLocation}.skipDescription`),
  skipDescriptionLink: t(`${cmsLocation}.skipDescriptionLink`),
  completeIdentity: t(`${cmsLocation}.completeIdentity`),
});

const Konfir = ({
  data,
  onSubmit,
  values: initialValues,
  ...props
}: DefaultIncomeQuestionComponentProps) => {
  const { t } = useTranslation();
  const content = useContent(t);
  const { broadcastEvent } = useAnalyticsEvent();

  const [checked, setChecked] = useState(false);
  const [error, setError] = useState('');
  const { birthdate } = initialValues;

  const handleSubmit = useCallback(() => {
    broadcastEvent('Clicked Konfir redirect');
    onSubmit({ key: stepName, value: null });
  }, [onSubmit, broadcastEvent]);

  const handleIdentitySubmit = useCallback(() => {
    window.location.replace('/questions/identity');
  }, []);

  const onClickHandler = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (!checked) {
      e.preventDefault();
      setError('Please confirm UK employment');
    } else {
      e.preventDefault();
      broadcastEvent('Clicked Konfir Button');
      goToKonfirAuthUrl({ uuid: initialValues.uuid || '', ukEmployment: checked });
    }
  };

  return (
    <QuestionOrganism
      id={stepName}
      data-testid={stepName}
      onSubmit={handleSubmit}
      title={content.title}
      subtitle={''}
      isFooterHidden
      {...props}
    >
      <div className="grid grid-cols-1 md:grid-cols-12">
        <div className="col-span-full md:col-span-8 flex flex-col space-y-6 lg:space-y-8 mb-8 lg:mb-12 mt-4">
          <p className="lg:pt-4">
            <Trans
              i18nKey={`${cmsLocation}.description`}
              components={{
                b: <b />,
              }}
            />
          </p>
        </div>
        {birthdate && (
          <div className="w-full md:col-span-8">
            <div className="w-full md:col-span-8">
              <Field data-testid="employed-in-uk-checkbox" validationError={error} className="ml-9">
                <label htmlFor="employed-in-uk-checkbox" className="text-sm flex items-center">
                  <Checkbox
                    id="employed-in-uk-checkbox"
                    data-testid="employed-in-uk-checkbox"
                    className="-ml-9"
                    checked={checked}
                    onChange={e => setChecked(e.currentTarget.checked)}
                    color="blue"
                  />

                  <span className="ml-4 text-sm">
                    <Trans
                      i18nKey={`${cmsLocation}.ukEmployment`}
                      components={{ r: <span className="text-red-500" /> }}
                    />
                  </span>
                </label>
              </Field>
            </div>
            <div className="w-full md:col-span-8">
              <div className="flex flex-col md:flex-row md:space-x-4 mt-3 mb-8">
                <Button
                  onClick={onClickHandler}
                  className="flex items-center justify-center w-full md:w-auto"
                >
                  {content.button}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {!birthdate && (
        <div className="w-full md:col-span-8">
          <span className="text-sm">
            <TextButton onClick={handleIdentitySubmit}>{content.completeIdentity}</TextButton>
          </span>
        </div>
      )}
      <div>
        <p className="flex border-t-2 pt-8" data-testid="manual-upload">
          <ExclamationCircleIcon className="w-5 h-5 mr-5 flex-shrink-0" />
          <span className="text-sm">
            <div className="mb-6">
              <Trans
                i18nKey={`${cmsLocation}.skipDescription`}
                components={{
                  b: <b />,
                }}
              />
            </div>
            <TextButton onClick={handleSubmit}>{content.skipDescriptionLink}</TextButton>
          </span>
        </p>
      </div>
    </QuestionOrganism>
  );
};

export default Konfir;
